<template>
    <div
        class="overflow-x-auto rounded-xl shadow-sm border border-gray-200 bg-white print:overflow-visible print:border-0 print:shadow-none">

        <!-- Desktop Grid View -->
        <div class="hidden md:grid min-w-[800px] print:min-w-full print:w-full print:block" :style="gridStyle">
            <!-- Header Row (Days) -->
            <div class="sticky top-0 z-10 bg-gray-50 border-b border-gray-200"></div> <!-- Corner Cell -->
            <div v-for="day in days" :key="day"
                class="sticky top-0 z-10 bg-gray-50 p-4 border-b border-gray-200 font-semibold text-gray-700 text-center uppercase text-sm tracking-wider print:bg-white print:border-black print:text-black print:border">
                {{ day }}
            </div>

            <!-- Time Slots and Entries -->
            <template v-if="slots.length > 0">
                <template v-for="slot in slots" :key="slot.id">
                    <!-- Time Slot Column/Header -->
                    <div
                        class="p-4 border-b border-r border-gray-100 bg-gray-50 font-medium text-gray-600 text-xs flex flex-col justify-center items-center text-center print:bg-white print:border-black print:text-black print:border-r print:border-b">
                        <span class="block text-sm text-gray-900 mb-1">{{ slot.name }}</span>
                        <span class="text-gray-500">{{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time)
                            }}</span>
                        <span v-if="slot.type === 'break'"
                            class="mt-2 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-[10px] uppercase font-bold tracking-wide">Break</span>
                    </div>

                    <!-- Content Cells for the Row -->
                    <div v-if="slot.type === 'break'" class="col-span-1 bg-stripes-gray border-b border-gray-100"
                        :style="{ gridColumn: `2 / span ${days.length}` }">
                        <!-- Break row spanning all days -->
                        <div class="h-full w-full flex items-center justify-center text-gray-400 text-sm italic py-4">
                            {{ slot.name || 'Break' }}
                        </div>
                    </div>

                    <template v-else>
                        <div v-for="day in days" :key="`${day}-${slot.id}`"
                            class="p-2 border-b border-r border-gray-100 relative group transition-colors hover:bg-gray-50 min-h-[100px] print:border-black print:border-r print:border-b"
                            :class="{ 'cursor-pointer hover:shadow-inner': editable || getEntry(day, slot.id) }"
                            @click="handleCellClick(day, slot)">
                            <div v-if="getEntry(day, slot.id)"
                                class="h-full w-full p-3 rounded-lg border-l-4 shadow-sm flex flex-col gap-1 transition-all hover:-translate-y-0.5 hover:shadow-md bg-white print:shadow-none print:border print:border-black"
                                :style="{ borderLeftColor: getSubjectColor(teachableName(getEntry(day, slot.id))) }">
                                <span class="font-bold text-gray-900 text-sm line-clamp-2">
                                    {{ teachableName(getEntry(day, slot.id)) || getEntry(day, slot.id)?.custom_title ||
                                        `No ${labels.SUBJECT}` }}
                                </span>
                                <span v-if="!hideStaff" class="text-xs text-gray-500 flex items-center gap-1" data-testid="timetable-staff">
                                    <i class="pi pi-user text-[10px]"></i>
                                    {{ getEntry(day, slot.id)?.staff?.user?.name || `No ${labels.TEACHER}` }}
                                </span>
                                <span v-if="getEntry(day, slot.id)?.room_name"
                                    class="text-xs text-gray-400 flex items-center gap-1 mt-auto">
                                    <i class="pi pi-map-marker text-[10px]"></i>
                                    {{ getEntry(day, slot.id)?.room_name }}
                                </span>
                            </div>
                            <div v-else-if="editable"
                                class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none print:hidden">
                                <span class="bg-gray-200 text-gray-600 rounded-full p-2 shadow-sm">
                                    <i class="pi pi-plus"></i>
                                </span>
                            </div>
                        </div>
                    </template>
                </template>
            </template>

            <div v-else class="col-span-full p-8 text-center text-gray-500 italic">
                No time slots defined. Please configure the timetable template.
            </div>

        </div>

        <!-- Mobile List View -->
        <div class="md:hidden flex flex-col divide-y divide-gray-200">
             <div v-if="slots.length === 0" class="p-8 text-center text-gray-500 italic">
                No time slots defined.
            </div>

            <div v-else v-for="day in days" :key="day" class="flex flex-col">
                <div class="bg-gray-100 px-4 py-2 font-bold text-gray-700 uppercase text-xs tracking-wider sticky top-0 z-10">
                    {{ day }}
                </div>

                <div v-if="getDayEntries(day).length === 0" class="p-4 text-center text-gray-400 text-sm italic bg-white">
                    No classes scheduled
                </div>

                <div v-for="item in getDayEntries(day)" :key="item.slot.id" class="p-4 bg-white flex flex-col gap-2 relative">
                    <!-- Break display -->
                    <div v-if="item.slot.type === 'break'" class="flex items-center justify-center p-2 bg-yellow-50 text-yellow-800 rounded text-sm italic">
                         <i class="pi pi-clock mr-2"></i> {{ item.slot.name }} ({{ formatTime(item.slot.start_time) }} - {{ formatTime(item.slot.end_time) }})
                    </div>

                    <!-- Entry display -->
                    <div v-else-if="item.entry"
                        class="p-3 rounded-lg border-l-4 shadow-sm flex flex-col gap-1 bg-white relative"
                        :style="{ borderLeftColor: getSubjectColor(teachableName(item.entry)) }"
                        @click="handleCellClick(day, item.slot)">

                        <div class="flex justify-between items-start">
                             <span class="font-bold text-gray-900 text-base">
                                {{ teachableName(item.entry) || item.entry.custom_title || `No ${labels.SUBJECT}` }}
                            </span>
                             <span class="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {{ formatTime(item.slot.start_time) }} - {{ formatTime(item.slot.end_time) }}
                            </span>
                        </div>

                        <span v-if="!hideStaff" class="text-sm text-gray-600 flex items-center gap-1 mt-1" data-testid="timetable-staff">
                            <i class="pi pi-user text-xs"></i>
                            {{ item.entry.staff?.user?.name || `No ${labels.TEACHER}` }}
                        </span>
                        <span v-if="item.entry.room_name"
                            class="text-xs text-gray-500 flex items-center gap-1">
                            <i class="pi pi-map-marker text-xs"></i>
                            {{ item.entry.room_name }}
                        </span>
                    </div>

                    <!-- Empty Slot in Editable Mode -->
                    <div v-else-if="editable"
                        class="p-4 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center text-gray-400 cursor-pointer hover:bg-gray-50"
                         @click="handleCellClick(day, item.slot)">
                        <div class="flex flex-col items-center gap-1">
                             <i class="pi pi-plus"></i>
                             <span class="text-xs">Add Class ({{ formatTime(item.slot.start_time) }})</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup lang="ts">
import type { TimetableEntry, TimetableTimeSlot } from '@/types/composition-functions';
import { teachableName } from '@/utils/teachable';
import { useSchoolLabels } from '@/composables/useSchoolLabels';

const { labels: labelsRef } = useSchoolLabels();
const labels = computed(() => labelsRef.value);

const props = defineProps<{
    slots: TimetableTimeSlot[];
    entries: TimetableEntry[];
    days: string[];
    editable?: boolean;
    /** Leave out the teacher line where staff names are not shared (the parent portal). */
    hideStaff?: boolean;
}>();

const emit = defineEmits<{
    (e: 'cell-click', payload: { day: string; slot: TimetableTimeSlot; entry?: TimetableEntry }): void;
}>();

const gridStyle = computed(() => ({
  gridTemplateColumns: `100px repeat(${props.days.length}, minmax(180px, 1fr))`,
}));

function formatTime(time: string) {
  if (!time) return '';
  // Assuming time comes as "HH:mm:ss" or similar
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours || '0', 10);
  const m = parseInt(minutes || '0', 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const formattedHours = h % 12 || 12;
  return `${formattedHours}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function getEntry(day: string, slotId: string) {
  return props.entries.find(
    (e) => e.day_of_week?.toLowerCase() === day.toLowerCase() && e.timeSlot?.id === slotId,
  );
}

function handleCellClick(day: string, slot: TimetableTimeSlot) {
  const entry = getEntry(day, slot.id);
  if (!props.editable && !entry) return;
  emit('cell-click', { day, slot, entry });
}

// Simple hash function for consistent colors
function getSubjectColor(subjectName: string) {
  if (!subjectName) return '#ccc';
  const colors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
    '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6',
  ];
  let hash = 0;
  for (let i = 0; i < subjectName.length; i++) {
    hash = subjectName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

// Mobile Helper: Get all slots for a specific day to iterate purely
function getDayEntries(day: string) {
  return props.slots.map(slot => {
    const entry = getEntry(day, slot.id);
    return { slot, entry };
  });
}
</script>

<style scoped>
/* Additional specific styles if tailwind isn't enough */
.bg-stripes-gray {
    background-image: linear-gradient(45deg, #f9fafb 25%, #f3f4f6 25%, #f3f4f6 50%, #f9fafb 50%, #f9fafb 75%, #f3f4f6 75%, #f3f4f6 100%);
    background-size: 20px 20px;
}
</style>
